import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { ChevronDown, ChevronRight } from 'lucide-react';

// ENH-04bis — liste par catégorie ACTIONNABLE sous la jauge d'un scope.
// 2 états À L'AFFICHAGE : ouvert (gris) / terminé (vert) — le BE garde 3 états
// (empty/in_progress/completed), le FE fusionne empty+in_progress = "ouvert".
// 2 sections : (1) ouvertes (action contextuelle) ; (2) terminées repliées →
// Rouvrir. Tonnage CO₂ par catégorie via category_emissions + formatChartValue
// (MÊME formateur adaptatif que les cartes scope → jamais d'unité mixte).
// data.categories = open_cats uniquement (exclues filtrées BE).

const STATE_LABEL_KEY = {
  empty: 'dashboard.completion.empty',
  in_progress: 'dashboard.completion.inProgress',
  completed: 'dashboard.completion.completed',
};

// FIX 3b : libellés VERBES. empty + in_progress → même action "Marquer terminée".
const ACTION_LABEL_KEY = {
  empty: 'dashboard.completion.markComplete',
  in_progress: 'dashboard.completion.markComplete',
  completed: 'common.reopen',
};

const CategoryCompletionList = ({ categories, getCategoryByCode, language, categoryEmissions, formatChartValue, onToggle, togglingCode }) => {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const [showDone, setShowDone] = useState(false);

  if (!categories || categories.length === 0) return null;

  const open = categories.filter((c) => c.state !== 'completed');
  const done = categories.filter((c) => c.state === 'completed');

  const nameOf = (code) => {
    const meta = getCategoryByCode?.(code);
    return meta ? (language === 'de' ? meta.name_de : meta.name_fr) : code;
  };

  // Tonnage : même formateur adaptatif que les cartes scope (ENH-02, jamais mixte).
  const tonnage = (code) => (formatChartValue ? formatChartValue(categoryEmissions?.[code] || 0) : '');

  // Pastille BICOLORE : terminé=vert, ouvert=gris (fusionne empty + in_progress).
  const Dot = ({ state }) => (
    <span
      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
        state === 'completed' ? 'bg-green-500' : isDark ? 'bg-slate-500' : 'bg-gray-300'
      }`}
      title={t(STATE_LABEL_KEY[state] || STATE_LABEL_KEY.empty)}
    />
  );

  const ActionButton = ({ cat }) => {
    const busy = togglingCode === cat.code;
    return (
      <button
        type="button"
        onClick={() => onToggle?.(cat.code)}
        disabled={busy || !onToggle}
        className={`text-xs font-medium px-2 py-0.5 rounded-lg whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          isDark ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
        }`}
      >
        {busy ? '…' : t(ACTION_LABEL_KEY[cat.state] || ACTION_LABEL_KEY.empty)}
      </button>
    );
  };

  const Row = ({ cat, muted }) => (
    <li className="flex items-center gap-2 text-sm">
      <Dot state={cat.state} />
      <span className={`flex-1 truncate ${muted ? (isDark ? 'text-slate-400' : 'text-gray-500') : (isDark ? 'text-slate-300' : 'text-gray-700')}`}>
        {nameOf(cat.code)}
        <span className={isDark ? 'text-slate-500' : 'text-gray-400'}> ({tonnage(cat.code)})</span>
      </span>
      <ActionButton cat={cat} />
    </li>
  );

  return (
    <div className="mt-3">
      {/* Section catégories ouvertes (empty + in_progress fusionnés) */}
      {open.length > 0 && (
        <ul className="space-y-1.5">
          {open.map((cat) => <Row key={cat.code} cat={cat} />)}
        </ul>
      )}

      {/* Section terminées (repliée par défaut) */}
      {done.length > 0 && (
        <div className={open.length > 0 ? 'mt-2' : ''}>
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            aria-expanded={showDone}
            className={`flex items-center gap-1 text-xs font-medium transition-colors ${
              isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {showDone ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {t('dashboard.completion.doneCount').replace('{count}', done.length)}
          </button>
          {showDone && (
            <ul className="mt-1.5 space-y-1.5">
              {done.map((cat) => <Row key={cat.code} cat={cat} muted />)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryCompletionList;
